import { useEffect, useState } from "react";
import { fileUploadApiKeyStore } from "../../../../api/file-upload-key-store";
import { gridApiKeyStore } from "../../../../api/grid-api-key-store";
import { SiTrackGrid } from "../../../../components/si-track-grid/si-track-grid";
import { useLoadData } from "../../../../hooks/useLoadData";
import { useSaveData } from "../../../../hooks/useSaveData";
import { ApiResponse } from "../../../../models/api-response";
import { UserPermissionName } from "../../../../models/user-permission-name";
import { GridColumnConfig, GridPreference } from "../../../../models/gridCoumnConfig";
import { State } from "@progress/kendo-data-query";
import useDebounce from "../../../../hooks/use-debounce";
import { fileColumns } from "./grid-columns";
import { useFileDownload } from "../../../../components/file-download";
import { Button, Grid, Typography } from "@mui/material";
import { GuardedComponent } from "../../../../components/guarded-component";
import { FileUploadDialog } from "./file-upload.dialog";

interface FileUploadComponent {
    title: string;
    unitID: number;
}

export const FileUploadComponent = ({ title, unitID }: FileUploadComponent) => {

    const [editItem, setEditItem] = useState<any>();
    const { downloadFile, isLoading } = useFileDownload();


    const handleDialogClosed = () => { setEditItem(undefined); }

    const handleUploadFile = () => { setEditItem({}); }

    const handleEditFile = (value: any) => {
        setEditItem(value);
    };

    const handleDownloadFile = async (data: any) => {
        downloadFile({ additionalData: data });
    };

    // -------------------------------------------------Grid Layout------------------------------
    const titleName = title?.split(" ")?.join("");
    const {
        isLoading: isLoadingGridLayout,
        data: gridLayouts,
        refetch: refetchGridLayout
    } = useLoadData<ApiResponse<GridPreference>>(
        gridApiKeyStore.getGridLayout(`${titleName}PlanReviewFile`, `${titleName}PlanReviewFile`)
    );

    const { isLoading: isFileUploadLoading, isFetching: isFileUploadFetching, data: UnitDetailFiles, refetch } = useLoadData<ApiResponse<any[]>>(fileUploadApiKeyStore.getFiles({ unitID: unitID }));

    const { mutateAsync } = useSaveData<any>(gridApiKeyStore.saveGridLayOut());
    const [gridConfig, setGridConfig] = useState<{
        dataState: State | undefined;
        columns: any[] | undefined;
    }>();

    const [filteredUnitDetailFiles, setFilteredUnitDetailFiles] = useState<any[]>([]);

    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearchTerm = useDebounce(searchTerm, 300);

    useEffect(() => {
        const filteredUnitDetailFileList = UnitDetailFiles?.data?.filter((UnitDetailFile) =>
            Object.values(UnitDetailFile).some(
                (value) => value && value.toString().toLowerCase().includes(debouncedSearchTerm.toLowerCase())
            )
        );
        setFilteredUnitDetailFiles(filteredUnitDetailFileList || []);
    }, [debouncedSearchTerm, UnitDetailFiles?.data]);

    const handleInputChange = (value: string) => {
        setSearchTerm(value);
    };

    const handleSaveLayout = (gridDataState: State, gridColumns: GridColumnConfig[]) => {
        const data = {
            filterJSON: JSON.stringify(gridDataState),
            columnJSON: JSON.stringify(gridColumns),
            gridName: `${titleName}PlanReviewFile`,
            pageName: `${titleName}PlanReviewFile`,
        };

        mutateAsync({ data }).then(() => {
            refetchGridLayout();
        });
    };

    const handleResetLayout = () => {
        const data = {
            filterJSON: null,
            columnJSON: null,
            gridName: `${titleName}PlanReviewFile`,
            pageName: `${titleName}PlanReviewFile`,
        };

        mutateAsync({ data }).then(() => {
            refetchGridLayout();
        });
    };

    useEffect(() => {
        if (!gridLayouts?.data) return;

        try {
            const filterData = gridLayouts.data.filterJSON ? JSON.parse(gridLayouts.data.filterJSON) : null;

            const columnJson = gridLayouts.data.columnJSON ? JSON.parse(gridLayouts.data.columnJSON) : null;

            // if (filterData && columnJson) {
            setGridConfig({
                columns: columnJson,
                dataState: filterData,
            });
            // }
        } catch (error) {
            console.log('Error parsing grid layout JSON:', error);
        }
    }, [gridLayouts?.data]);

    // ----------------------------------------------------------------------------------------
    return (
        <Grid container spacing={1}>
            <Grid className="subHead" item lg={12} sm={12} xl={12} xs={12}>
                <Typography variant="h3" color="textPrimary">
                    UPLOADED FILES
                </Typography>
            </Grid>
            <Grid item lg={12} sm={12} xl={12} xs={12}>
                <SiTrackGrid
                    fileTitle='file_upload'
                    dataItemKey="fileID"
                    currentPageSize={5}
                    columns={fileColumns}
                    onDownload={(data: any) => {
                        handleDownloadFile(data);
                    }}
                    onEdit={handleEditFile}
                    deleteMutationConfig={(item) => fileUploadApiKeyStore.deleteFile(item.fileID)}
                    actionPermissions={{
                        edit: UserPermissionName.EDIT_FILE,
                        download: UserPermissionName.DOWNLOAD_FILE,
                        export: UserPermissionName.DOWNLOAD_GRID
                    }}
                    isBusy={isLoading || isFileUploadLoading || isFileUploadFetching || isLoadingGridLayout}

                    // -----------------Grid Layout-------
                    dataItems={filteredUnitDetailFiles || []}
                    reorderable
                    handleSearch={handleInputChange}
                    handleSaveLayout={handleSaveLayout}
                    isSaveLayout={true}
                    gridConfigFromApi={gridConfig}
                    handleResetLayout={handleResetLayout}
                // -----------------Grid Layout-------
                />
            </Grid>
            <GuardedComponent requiredPermissions={[UserPermissionName.UPLOAD_FILE]}>
                <Grid item lg={12} sm={12} xl={12} xs={12}>
                    <Grid
                        container
                        direction="row"
                        sx={{
                            justifyContent: "flex-end",
                            alignItems: "flex-start",
                        }}
                    >
                        <Button variant="contained" style={{ width: '130px' }} onClick={handleUploadFile}>
                            ADD FILES
                        </Button>
                    </Grid>
                </Grid>
            </GuardedComponent>
            {editItem && (
                <FileUploadDialog
                    unitID={unitID}
                    fileData={editItem}
                    onClosed={handleDialogClosed} />
            )}
        </Grid>
    );
}